import useCurrentMerchant from 'hooks/useCurrentMerchant'
import { asyncWithLDProvider, useLDClient } from 'launchdarkly-react-client-sdk'
import React, { useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { selectCurrentMerchantUser } from 'selectors/merchantUser'

type Props = {
  children: React.ReactNode
}

/**
 * The placeholder LD context used before (and outside of) a merchant session.
 */
const LOGGED_OUT_KEY = 'merchant-logged-out'

/**
 * How long to wait for the merchant / MerchantUser entities before giving up
 * and releasing consumers onto whatever flags are on hand.
 *
 * Those entities normally land in well under a second. If they never arrive the
 * app is already broken, and holding a spinner forever is strictly worse than
 * falling through — the user at least gets a page (or an honest redirect)
 * rather than an indefinite hang with no way out but a reload.
 */
export const ENTITY_LOAD_TIMEOUT_MS = 10_000

/**
 * Delay before retrying a failed identify(). Without a retry, a transient
 * failure permanently parks the app on the previous context's flag values
 * (usually the logged-out defaults) until reload or merchant switch.
 */
export const IDENTIFY_RETRY_DELAY_MS = 3_000

/**
 * Cap on identify() attempts per context key (1 initial + 2 retries). Bounded
 * because users whose ad blocker blocks LaunchDarkly outright fail every
 * attempt — master's unbounded every-render retry was a request storm for
 * exactly those users.
 */
export const MAX_IDENTIFY_ATTEMPTS = 3

/**
 * Whether LaunchDarkly has evaluated flags for the context we actually want —
 * i.e. `merchant-<id>` for a signed-in session, or `merchant-logged-out` when
 * there genuinely is no session.
 *
 * This exists because `useFlag` is `useFlags()[key] ?? defaultValue`, which
 * cannot distinguish "this flag is off" from "LD has not evaluated this context
 * yet". Both read false, so anything that *redirects* on a false flag will
 * bounce the user out of the page on a hard refresh unless it waits for this.
 * Reading a flag for rendering (show/hide a nav link) is fine without it — the
 * value simply corrects itself; it is the irreversible actions (redirects) that
 * must wait.
 *
 * Defaults to `true` so a tree rendered without this provider (unit tests,
 * Storybook) never hangs waiting on a signal nobody publishes.
 */
export const FlagsReadyContext = React.createContext<boolean>(true)

/**
 * Non-functional dummy provider used if we encounter an exception during
 * initialization
 */
const DummyProvider: React.FC = ({ children }) => {
  return <>{children}</>
}

/**
 * Determines the LaunchDarkly user object from the application state
 * Returns null when the app is still initialising so we can wait and avoid
 * state changing immediately after loading, which can present as a "flicker"
 */
const UseLaunchDarklyUser = () => {
  const merchant = useCurrentMerchant()
  const user = useSelector(selectCurrentMerchantUser)
  if (merchant && user) {
    // Logged in
    return { key: `merchant-${merchant!.id}` }
  } else if (!user) {
    // Logged out
    return { key: LOGGED_OUT_KEY }
  }
  // App is still initialising
  return null
}

/**
 * Updates the LaunchDarkly user if the user changes after initialization.
 * For example: when a logged out user logs in. This is a separate component
 * as useLDClient is only accessible in children of the FlagsProvider
 */
const UserUpdater: React.FC<Props> = ({ children }) => {
  const ldClient = useLDClient()
  const launchDarklyUser = UseLaunchDarklyUser()
  // A session exists as soon as the persisted auth state carries a merchant
  // user id — which happens well before the MerchantUser ORM entity that
  // UseLaunchDarklyUser keys on has been fetched. That gap is precisely the
  // window in which LD is still on LOGGED_OUT_KEY while the user is in fact
  // signed in.
  const hasSession = useSelector(
    (state: any) => state.auth.current_merchant_user_id != null
  )
  const [areFlagsReady, setAreFlagsReady] = useState(false)
  // The context key we have finished identifying to. A ref (not state) because
  // it guards the effect body itself: `launchDarklyUser` is a fresh object
  // literal every render, so the effect re-runs constantly. `inFlightKeyRef`
  // exists because a getUser()-comparison alone can't tell "identify is
  // already in flight for this key" apart from "identify is still needed for
  // this key" — without it, re-renders while an identify is pending would
  // double-fire a duplicate identify() for the same key.
  const settledKeyRef = useRef<string | null>(null)
  const inFlightKeyRef = useRef<string | null>(null)

  const launchDarklyUserKey = launchDarklyUser?.key ?? null

  // The context LD *should* be evaluating. Signing out makes LOGGED_OUT_KEY the
  // desired context in its own right — not merely a placeholder — so logging
  // out mid-session re-identifies rather than leaving the client parked on the
  // previous merchant and serving their flag values to whoever logs in next.
  const desiredKey = hasSession ? launchDarklyUserKey : LOGGED_OUT_KEY

  // Signed in, but UseLaunchDarklyUser is still returning the placeholder
  // because the merchant / MerchantUser entities have not landed. The key alone
  // cannot distinguish this from a genuine sign-out, which is why it is derived
  // from `hasSession` rather than read off the key.
  const isAwaitingEntities =
    hasSession && launchDarklyUserKey === LOGGED_OUT_KEY

  // Lets a settling identify tell whether it is still the one that matters.
  const desiredKeyRef = useRef<string | null>(desiredKey)
  desiredKeyRef.current = desiredKey

  // Attempt count for the identify currently being retried, keyed to the
  // context key it belongs to — a new desired key starts with a fresh budget.
  const attemptsRef = useRef<{ key: string | null; count: number }>({
    key: null,
    count: 0,
  })
  // Bumped to re-trigger the effect below after a bounded retry delay.
  const [retryNonce, setRetryNonce] = useState(0)
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Unmount-only: a pending retry timer must not fire setRetryNonce on an
  // unmounted component. Deliberately not in the main effect's cleanup — a
  // retry timer firing after the desired key has since changed is harmless
  // (the effect just re-evaluates current state), so there is no need to
  // cancel it on every effect re-run, only on unmount.
  useEffect(() => {
    return () => {
      if (retryTimerRef.current) clearTimeout(retryTimerRef.current)
    }
  }, [])

  useEffect(() => {
    // LD failed to initialise (DummyProvider): useFlags() is {} forever, so
    // every flag is its default and no identify will ever land. Release
    // consumers onto those defaults rather than holding them indefinitely.
    if (!ldClient) {
      setAreFlagsReady(true)
      return
    }

    if (isAwaitingEntities || !desiredKey) return

    if (settledKeyRef.current === desiredKey) {
      setAreFlagsReady(true)
      return
    }

    // Nothing to wait for at boot: asyncWithLDProvider has already fetched the
    // payload for the context it initialised with, so if that is the context we
    // want, re-identifying would spend a round trip to learn what we already
    // hold. Safe to trust getUser() here because the SDK only updates it
    // (`setUser`, inside identify()) after fetchFlagSettings() resolves — never
    // synchronously and never on failure — so a key it reports is one whose
    // payload has actually been fetched and applied, not merely requested.
    // Gated on nothing of ours being settled OR in flight so this only runs
    // once, at boot.
    if (
      settledKeyRef.current === null &&
      inFlightKeyRef.current === null &&
      ldClient.getUser()?.key === desiredKey
    ) {
      settledKeyRef.current = desiredKey
      setAreFlagsReady(true)
      return
    }

    if (inFlightKeyRef.current === desiredKey) return
    inFlightKeyRef.current = desiredKey

    // The context is about to change under consumers, so the flags they can
    // read right now belong to the outgoing one. Re-gate them.
    setAreFlagsReady(false)

    const identifyKey = desiredKey

    if (attemptsRef.current.key !== identifyKey) {
      attemptsRef.current = { key: identifyKey, count: 0 }
    }
    attemptsRef.current.count += 1
    const attemptNumber = attemptsRef.current.count

    const identifyUser = async () => {
      try {
        await ldClient.identify({ key: identifyKey })
        // A superseded identify must not publish: if the merchant switched from
        // A to B while A was in flight, A settling last would otherwise announce
        // "ready" while B's payload is still on the wire.
        if (desiredKeyRef.current === identifyKey) {
          settledKeyRef.current = identifyKey
          inFlightKeyRef.current = null
          setAreFlagsReady(true)
        } else if (inFlightKeyRef.current === identifyKey) {
          inFlightKeyRef.current = null
        }
      } catch (e) {
        if (process.env.NODE_ENV !== 'test') {
          /* eslint-disable-next-line no-console */
          console.error('Could not update LaunchDarkly', e)
        }
        if (inFlightKeyRef.current === identifyKey) {
          inFlightKeyRef.current = null
        }
        // Do NOT settle the key on failure — settling would permanently park
        // consumers on this (probably wrong) context's flag values. Instead,
        // release the readiness valve so consumers proceed on the values at
        // hand rather than hanging, and — if attempts remain — retry after a
        // bounded delay rather than leaving them parked until reload.
        if (desiredKeyRef.current === identifyKey) {
          setAreFlagsReady(true)
          if (attemptNumber < MAX_IDENTIFY_ATTEMPTS) {
            retryTimerRef.current = setTimeout(() => {
              setRetryNonce(n => n + 1)
            }, IDENTIFY_RETRY_DELAY_MS)
          }
        }
      }
    }
    identifyUser()
  }, [ldClient, desiredKey, isAwaitingEntities, retryNonce])

  // Bounded wait on the entities. Without this the effect above simply returns
  // while isAwaitingEntities holds, so entities that never arrive would park
  // every consumer on a spinner with no way out but a reload.
  useEffect(() => {
    if (!isAwaitingEntities) return

    const timer = setTimeout(
      () => setAreFlagsReady(true),
      ENTITY_LOAD_TIMEOUT_MS
    )
    return () => clearTimeout(timer)
  }, [isAwaitingEntities])

  return (
    <FlagsReadyContext.Provider value={areFlagsReady}>
      {children}
    </FlagsReadyContext.Provider>
  )
}

/**
 * Initializes LaunchDarkly, only once. Subsequent changes to the user
 * information just use the client to "identify" the user
 *
 * I'm not super happy with this imnplementation as it seems overly
 * complicated, but it satisfies the following use cases:
 *
 * 1. Whether logged in or logged out, we avoid a flicker on loading due to
 *    initialization. This can be tested by enabling a flag that should affect
 *    the rendering and reloading
 * 2. If an exception occurs during initialization, we still successfully render
 *    the application, with no feature flipping functionality. This can be
 *    tested by raising an exception within the try block
 * 3. Starting logged in and moving to logged out appropriately changes the flag
 *    state. This can be tested by enabling a flag for a specific user, logging
 *    out, reloading the app, logging in, and ensuring that whatever was gated
 *    by the flag is appropriately enabled
 * 4. We don't see any unexpected flickering between states when changing the
 *    flag, tested by enabling and disabling flags while viewing the app.
 * 5. Avoids creating any new warnings or errors in the console.
 * 6. Integrations tests pass even when LaunchDarkly is not configured. This is
 *    important as integration tests shouldn't be interacting with LaunchDarkly
 *    flipper state.
 *
 * As such, I feel this code is robust, which compensates for the complexity,
 * however, I'd love to see a cleaner implementation that handles the above
 * edge cases. Using the Javascript SDK instead of the React SDK may give us
 * more power here.
 */
const FlagsProvider: React.FC<Props> = ({ children }) => {
  const launchDarklyUser = UseLaunchDarklyUser()
  const [LDProvider, setLDProvider] = useState<null | any>(null)

  useEffect(() => {
    const initializeLaunchDarklyProvider = async user => {
      try {
        const Provider = await asyncWithLDProvider({
          clientSideID: process.env.REACT_APP_LAUNCH_DARKLY_CLIENT_SIDE_ID!,
          reactOptions: { useCamelCaseFlagKeys: false },
          options: { bootstrap: 'localStorage' },
          user: user,
        })
        setLDProvider(() => Provider)
      } catch (e) {
        // If an exception is thrown during initialization we should render
        // with a "dummy provider", so the page still loads albeit with
        // default/null flags
        if (process.env.NODE_ENV !== 'test') {
          /* eslint-disable-next-line no-console */
          console.error('Could not initalize LaunchDarkly', e)
        }
        setLDProvider(() => DummyProvider)
      }
    }

    if (LDProvider) {
      // We don't want to re-initialize the Launch Darkly provider if it
      // already has been initialized
      return
    }
    if (!launchDarklyUser) {
      // We don't want to initialize the Launch Darkly provider if the rest of
      // the app is still initializing
      return
    }
    initializeLaunchDarklyProvider(launchDarklyUser)
  }, [launchDarklyUser, LDProvider])

  if (!LDProvider) {
    // We want to avoid rendering anything until LD is initalized to avoid any
    // "flickering" caused by flag values changing upon initialization
    return null
  }

  return (
    <LDProvider>
      <UserUpdater>{children}</UserUpdater>
    </LDProvider>
  )
}

export default FlagsProvider
